/* eslint-disable no-await-in-loop */
import Cookies from 'js-cookie'
import jwtDefaultConfig from './jwtDefaultConfig'

const refreshLockName = 'hdg:office:refresh-token'
const refreshStorageLockKey = `${refreshLockName}:lock`
const refreshWaitTtlMs = 12000
const refreshLockPollMs = 100
const refreshLockLeaseMs = 15000
const retryableRefreshStatuses = [409, 429, 503]

const hasWebLocks = () => typeof navigator !== 'undefined'
  && navigator.locks
  && typeof navigator.locks.request === 'function'

const wait = delay => new Promise(resolve => {
  window.setTimeout(resolve, delay)
})

const getSessionFailureReason = response => {
  const { data } = response || {}
  return data && typeof data === 'object' && data.reason ? data.reason : ''
}

const isRetryableRefreshError = error => {
  const status = error && error.response ? error.response.status : 0
  return retryableRefreshStatuses.includes(status)
}

const isUnauthorizedRefreshError = error => error
  && error.response
  && error.response.status === 401

const readStorageRefreshLock = () => {
  try {
    const value = localStorage.getItem(refreshStorageLockKey)
    return value ? JSON.parse(value) : null
  } catch (error) {
    return null
  }
}

const tryAcquireStorageRefreshLock = owner => {
  try {
    const now = Date.now()
    const current = readStorageRefreshLock()
    if (current && current.expiresAt > now && current.owner !== owner) return false

    localStorage.setItem(refreshStorageLockKey, JSON.stringify({
      owner,
      expiresAt: now + refreshLockLeaseMs,
    }))

    const stored = readStorageRefreshLock()
    return stored && stored.owner === owner
  } catch (error) {
    return true
  }
}

const releaseStorageRefreshLock = owner => {
  try {
    const current = readStorageRefreshLock()
    if (current && current.owner === owner) {
      localStorage.removeItem(refreshStorageLockKey)
    }
  } catch (error) {
    // Storage lock cleanup is best effort.
  }
}

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  refreshAccessTokenPromise = null

  authEpoch = 0

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        // eslint-disable-next-line no-param-reassign
        config.authTokenAtRequest = accessToken || ''
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const {
          config,
          response,
        } = error
        const originalRequest = config || {}

        if (response && response.status === 401) {
          if (originalRequest.authTokenAtRequest && originalRequest.authTokenAtRequest !== this.getToken()) {
            return Promise.reject(error)
          }

          if (originalRequest.sessionAware && getSessionFailureReason(response)) {
            return Promise.reject(error)
          }

          if (originalRequest.sessionRetry || this.isRefreshRequest(originalRequest) || !this.getRefreshToken()) {
            this.removeToken()
            if (window.location.pathname !== '/login') window.location.href = '/login'
            return Promise.reject(error)
          }

          originalRequest.sessionRetry = true
          return this.refreshAccessToken()
            .then(accessToken => {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
              return this.axiosIns(originalRequest)
            })
            .catch(refreshError => {
              if (!this.getRefreshToken() || isUnauthorizedRefreshError(refreshError)) {
                this.removeToken()
                if (window.location.pathname !== '/login') window.location.href = '/login'
              }
              return Promise.reject(refreshError || error)
            })
        }
        return Promise.reject(error)
      },
    )
  }

  getToken() {
    return Cookies.get(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return Cookies.get(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    // const deleteTime = new Date(new Date().getTime() + this.accessTokenTime * 60 * 1000)
    Cookies.set(this.jwtConfig.storageTokenKeyName, value, {
      secure: true,
    })
  }

  setRefreshToken(value) {
    Cookies.set(this.jwtConfig.storageRefreshTokenKeyName, value, {
      secure: true,
    })
  }

  isRefreshRequest(config) {
    const url = config && config.url ? `${config.url}` : ''
    return url === this.jwtConfig.refreshEndpoint || url.endsWith('/auth/refresh-token')
  }

  waitForSharedRefresh(originalRefreshToken, authEpoch, timeoutMs = refreshWaitTtlMs) {
    return new Promise((resolve, reject) => {
      const deadline = Date.now() + timeoutMs
      const poll = () => {
        if (this.authEpoch !== authEpoch) {
          reject(new Error('Refresh cancelled'))
          return
        }

        const nextRefreshToken = this.getRefreshToken()
        const nextAccessToken = this.getToken()
        if (!nextRefreshToken) {
          reject(new Error('Missing refresh token'))
          return
        }

        if (nextRefreshToken !== originalRefreshToken && nextAccessToken) {
          resolve(nextAccessToken)
          return
        }

        if (Date.now() >= deadline) {
          resolve(null)
          return
        }

        window.setTimeout(poll, refreshLockPollMs)
      }

      poll()
    })
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  loginCaptcha(...args) {
    return this.axiosIns.post(this.jwtConfig.loginCaptchaEndpoint, ...args)
  }

  loginTotp(...args) {
    return this.axiosIns.post(this.jwtConfig.loginTotpEndpoint, ...args)
  }

  loginSecurityTotpStatus(...args) {
    return this.axiosIns.post(this.jwtConfig.loginSecurityTotpStatusEndpoint, ...args)
  }

  loginSecurityTotpReset(...args) {
    return this.axiosIns.post(this.jwtConfig.loginSecurityTotpResetEndpoint, ...args)
  }

  loginSecurityTotpSelfChangeBegin(...args) {
    return this.axiosIns.post(this.jwtConfig.loginSecurityTotpSelfChangeBeginEndpoint, ...args)
  }

  loginSecurityTotpSelfChangeVerify(...args) {
    return this.axiosIns.post(this.jwtConfig.loginSecurityTotpSelfChangeVerifyEndpoint, ...args)
  }

  currentSession() {
    return this.axiosIns.get(this.jwtConfig.sessionMeEndpoint, { sessionAware: true })
  }

  recordSessionActivity(activityAt) {
    return this.axiosIns.post(this.jwtConfig.sessionActivityEndpoint, {
      activityAt: activityAt ? new Date(activityAt).toISOString() : null,
    }, { sessionAware: true })
  }

  loginSessions(params = {}) {
    return this.axiosIns.get(this.jwtConfig.sessionsEndpoint, { params })
  }

  revokeSession(sid, reason) {
    return this.axiosIns.post(`${this.jwtConfig.sessionsEndpoint}/${encodeURIComponent(sid)}/revoke`, {
      reason,
    })
  }

  revokeUserSessions(username, reason) {
    return this.axiosIns.post(`${this.jwtConfig.sessionsEndpoint}/user/${encodeURIComponent(username)}/revoke`, {
      reason,
    })
  }

  sessionPolicy() {
    return this.axiosIns.get(this.jwtConfig.sessionPolicyEndpoint)
  }

  updateSessionPolicy(data) {
    return this.axiosIns.put(this.jwtConfig.sessionPolicyEndpoint, data)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken(refreshToken = this.getRefreshToken()) {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken,
    })
  }

  refreshAccessToken() {
    const currentRefreshToken = this.getRefreshToken()
    if (!currentRefreshToken) {
      return Promise.reject(new Error('Missing refresh token'))
    }

    if (!this.refreshAccessTokenPromise) {
      const { authEpoch } = this
      this.refreshAccessTokenPromise = this.refreshAccessTokenWithLock(currentRefreshToken, authEpoch)
        .finally(() => {
          this.refreshAccessTokenPromise = null
        })
    }

    return this.refreshAccessTokenPromise
  }

  async refreshAccessTokenWithLock(currentRefreshToken, authEpoch) {
    if (!hasWebLocks()) {
      return this.refreshAccessTokenWithStorageLock(currentRefreshToken, authEpoch)
    }

    return navigator.locks.request(
      refreshLockName,
      { mode: 'exclusive' },
      async () => this.performRefresh(currentRefreshToken, authEpoch),
    )
  }

  async refreshAccessTokenWithStorageLock(currentRefreshToken, authEpoch) {
    if (typeof localStorage === 'undefined') {
      return this.performRefresh(currentRefreshToken, authEpoch)
    }

    const owner = `${Date.now()}:${Math.random()}`
    const deadline = Date.now() + refreshWaitTtlMs

    while (Date.now() < deadline) {
      const sharedAccessToken = await this.waitForSharedRefresh(currentRefreshToken, authEpoch, refreshLockPollMs)
      if (sharedAccessToken) return sharedAccessToken

      if (tryAcquireStorageRefreshLock(owner)) {
        try {
          return await this.performRefresh(currentRefreshToken, authEpoch)
        } finally {
          releaseStorageRefreshLock(owner)
        }
      }

      await wait(refreshLockPollMs)
    }

    return this.performRefresh(currentRefreshToken, authEpoch)
  }

  async performRefresh(currentRefreshToken, authEpoch) {
    const deadline = Date.now() + refreshWaitTtlMs
    let attempt = 0

    for (;;) {
      const latestRefreshToken = this.getRefreshToken()
      if (this.authEpoch !== authEpoch || !latestRefreshToken) {
        throw new Error('Refresh cancelled')
      }

      if (latestRefreshToken !== currentRefreshToken) {
        const accessToken = this.getToken()
        if (accessToken) return accessToken
        throw new Error('Missing access token')
      }

      try {
        const response = await this.refreshToken(latestRefreshToken)
        if (this.authEpoch !== authEpoch || !this.getRefreshToken()) {
          throw new Error('Refresh cancelled')
        }

        const { data } = response
        this.setToken(data.accessToken)
        this.setRefreshToken(data.refreshToken)
        return data.accessToken
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          this.removeToken()
        }

        if (!isRetryableRefreshError(error) || Date.now() >= deadline) {
          throw error
        }

        attempt += 1
        await wait(Math.min(250 * attempt, 1000))
      }
    }
  }

  logout() {
    return this.axiosIns.post(this.jwtConfig.logoutEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }

  removeToken() {
    this.authEpoch += 1
    this.refreshAccessTokenPromise = null
    Cookies.remove(this.jwtConfig.storageTokenKeyName)
    Cookies.remove(this.jwtConfig.storageRefreshTokenKeyName)
    localStorage.removeItem('userData')
  }
}
